import React, { ChangeEvent, FC, useState } from 'react';

import './ContentTypeParams.scss';
import { BubbleButton, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  Brand,
  BrandAttribute,
  BrandLevel,
  BrandSegment,
  BrandVenueType,
} from '@go-shared/types/brand';
import { Checkbox } from '@spark-labs/avocado-ui';
import { EventRundownTimeGrouping } from '@go-shared/types/contentTemplate';
import { BubbleTypeaheadOption, BubbleTypeaheadValue } from '../BubbleUI/BubbleTypeahead';

interface BaseProps {
  contentType: string;
}

interface FilteredRundownProps {
  segments: string[] | undefined;
  levels: string[] | undefined;
  attributes: string[] | undefined;
  venues: string[] | undefined;
  eventRundownTitle: string | undefined;
  eventRundownDeckPrefix: string | undefined;
  eventRundownRemoveTimeDash: boolean | undefined;
  eventRundownShowDescription: boolean | undefined;
  eventRundownTimeGrouping: EventRundownTimeGrouping[] | undefined;
  onSelectSegments: (selected: BubbleTypeaheadValue<BrandSegment>[]) => void;
  onSelectLevels: (selected: BubbleTypeaheadValue<BrandLevel>[]) => void;
  onSelectAttributes: (selected: BubbleTypeaheadValue<BrandAttribute>[]) => void;
  onSelectVenues: (selected: BubbleTypeaheadValue<BrandVenueType>[]) => void;
  setEventRundownTitle: (nextEventRundownTitle: string) => void;
  setEventRundownDeckPrefix: (nextEventRundownDeckPrefix: string) => void;
  handleEventRundownRemoveTimeDashChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleEventRundownShowDescriptionChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleTimeGroupingAdd: (timeGrouping: EventRundownTimeGrouping) => void;
  handleTimeGroupingDelete: (index: number) => void;
  setTimeGroupingName: (nextName: string, index: number) => void;
  setTimeGroupingStartTime: (nextStartTime: number, index: number) => void;
  eventRundownHideEndTime: boolean | undefined;
  handelEventRundownHideEndTime: (event: ChangeEvent<HTMLInputElement>) => void;
}

type Props = BaseProps & FilteredRundownProps;

export const ContentTypeParams: FC<Props> = props => {
  const { brand } = useServerInfo();
  const [selectedSegments, setSelectedSegments] = useState<BubbleTypeaheadValue<BrandSegment>[]>(
    getSegments(brand, props.segments)
  );
  const [selectedLevels, setSelectedLevels] = useState<BubbleTypeaheadValue<BrandLevel>[]>(
    getLevels(brand, props.levels)
  );
  const [selectedAttributes, setSelectedAttributes] = useState<
    BubbleTypeaheadValue<BrandAttribute>[]
  >(getAttributes(brand, props.attributes));
  const [selectedVenues, setSelectedVenues] = useState<BubbleTypeaheadValue<BrandVenueType>[]>(
    getVenues(brand, props.venues)
  );
  const [timeGroupingName, setTimeGroupingName] = useState('');
  const [timeGroupingStartTime, setTimeGroupingStartTime] = useState(0);

  const segmentOptions: BubbleTypeaheadOption<BrandSegment>[] = brand.segments.map(segment => ({
    key: segment._id,
    item: segment,
  }));
  const levelOptions: BubbleTypeaheadOption<BrandLevel>[] = brand.levels.map(level => ({
    key: level._id,
    item: level,
  }));
  const attributeOptions: BubbleTypeaheadOption<BrandAttribute>[] = brand.attributes.map(
    attribute => ({
      key: attribute._id,
      item: attribute,
    })
  );
  const venueOptions: BubbleTypeaheadOption<BrandVenueType>[] = brand.venueTypes.map(venue => ({
    key: venue._id,
    item: venue,
  }));

  switch (props.contentType) {
    case 'filtered-rundown':
    case 'multi-day':
      return getFilteredRundownParams();
    default:
      return <></>;
  }

  function getFilteredRundownParams(): React.JSX.Element {
    function handleOnAddSegment(value: BubbleTypeaheadValue<BrandSegment>) {
      const newSelectedSegments = [...selectedSegments, value];

      setSelectedSegments(newSelectedSegments);
      props.onSelectSegments(newSelectedSegments);
    }

    function handleOnRemoveSegment(value: BubbleTypeaheadValue<BrandSegment>) {
      const newSelectedSegments = selectedSegments.filter(ss => ss.key !== value.key);

      setSelectedSegments(newSelectedSegments);
      props.onSelectSegments(newSelectedSegments);
    }

    function handleOnAddLevel(value: BubbleTypeaheadValue<BrandLevel>) {
      const newSelectedLevels = [...selectedLevels, value];

      setSelectedLevels(newSelectedLevels);
      props.onSelectLevels(newSelectedLevels);
    }

    function handleOnRemoveLevel(value: BubbleTypeaheadValue<BrandLevel>) {
      const newSelectedLevels = selectedLevels.filter(ss => ss.key !== value.key);

      setSelectedLevels(newSelectedLevels);
      props.onSelectLevels(newSelectedLevels);
    }

    function handleOnAddAttribute(value: BubbleTypeaheadValue<BrandAttribute>) {
      const newSelectedAttributes = [...selectedAttributes, value];

      setSelectedAttributes(newSelectedAttributes);
      props.onSelectAttributes(newSelectedAttributes);
    }

    function handleOnRemoveAttribute(value: BubbleTypeaheadValue<BrandAttribute>) {
      const newSelectedAttributes = selectedAttributes.filter(ss => ss.key !== value.key);

      setSelectedAttributes(newSelectedAttributes);
      props.onSelectAttributes(newSelectedAttributes);
    }

    function handleOnAddVenues(value: BubbleTypeaheadValue<BrandVenueType>) {
      const newSelectedVenues = [...selectedVenues, value];

      setSelectedVenues(newSelectedVenues);
      props.onSelectVenues(newSelectedVenues);
    }

    function handleOnRemoveVenues(value: BubbleTypeaheadValue<BrandVenueType>) {
      const newSelectedVenues = selectedVenues.filter(ss => ss.key !== value.key);

      setSelectedVenues(newSelectedVenues);
      props.onSelectVenues(newSelectedVenues);
    }

    function handleTimeGroupingAdd() {
      const timeGrouping: EventRundownTimeGrouping = {
        name: timeGroupingName,
        startTime: timeGroupingStartTime,
      };
      props.handleTimeGroupingAdd(timeGrouping);
      setTimeGroupingName('');
      setTimeGroupingStartTime(0);
    }

    function isValidTimeGrouping(): boolean {
      return !!timeGroupingName && typeof timeGroupingStartTime === 'number';
    }

    return (
      <div className="border-t border-grey mt-6 p-4">
        <h2 className="text-center font-bold text-grey-dark text-lg">Filter</h2>
        <div className="flex">
          <div>
            <span>Segments</span>
            <BubbleTypeahead
              className="select-container"
              placeholder="Filter segments shown..."
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={opt => opt.name}
              value={selectedSegments}
              options={segmentOptions}
              onAdd={handleOnAddSegment}
              onRemove={handleOnRemoveSegment}
            />
          </div>
          <div>
            <span>Levels</span>
            <BubbleTypeahead
              className="select-container"
              placeholder="Filter levels shown..."
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={opt => opt.name}
              value={selectedLevels}
              options={levelOptions}
              onAdd={handleOnAddLevel}
              onRemove={handleOnRemoveLevel}
            />
          </div>
        </div>
        <div className="flex">
          <div>
            <span>Attributes</span>
            <BubbleTypeahead
              className="select-container"
              placeholder="Filter attributes shown..."
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={opt => opt.name}
              value={selectedAttributes}
              options={attributeOptions}
              onAdd={handleOnAddAttribute}
              onRemove={handleOnRemoveAttribute}
            />
          </div>
          <div>
            <span>Venues</span>
            <BubbleTypeahead
              className="select-container"
              placeholder="Filter venues shown..."
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={opt => opt.name}
              value={selectedVenues}
              options={venueOptions}
              onAdd={handleOnAddVenues}
              onRemove={handleOnRemoveVenues}
            />
          </div>
        </div>
        <div className="flex my-4">
          <div>
            <span className="font-bold text-grey-dark text-lg">Title</span>
            <BubbleInput
              block
              type="text"
              className="p-4 sticky top-0"
              value={props.eventRundownTitle}
              placeholder="eventRundownTitle"
              onValue={props.setEventRundownTitle}
            />
          </div>
          <div>
            <span className="font-bold text-grey-dark text-lg">Deck Prefix</span>
            <BubbleInput
              block
              type="text"
              className="p-4 sticky top-0"
              value={props.eventRundownDeckPrefix}
              placeholder="eventRundownDeckPrefix"
              onValue={props.setEventRundownDeckPrefix}
            />
          </div>
          <div className="pt-8">
            <Checkbox
              onChange={props.handleEventRundownRemoveTimeDashChange}
              value={'eventRundownRemoveTimeDash'}
              label={'Remove Time Dash'}
              checked={!!props.eventRundownRemoveTimeDash}
            />
            <br />
            <Checkbox
              onChange={props.handleEventRundownShowDescriptionChange}
              value={'eventRundownShowDescription'}
              label={'Show Description'}
              checked={!!props.eventRundownShowDescription}
            />
            <br />
            <Checkbox
              onChange={props.handelEventRundownHideEndTime}
              value={'eventRundownHideEndTime'}
              label={'Hide end time'}
              checked={!!props.eventRundownHideEndTime}
            />
          </div>
        </div>
        <h2 className="text-center font-bold text-grey-dark text-lg">Time Grouping</h2>
        <div className="grid grid-cols-3">
          <span>Name</span>
          <span>Start Time</span>
          <div></div>
        </div>
        {(props.eventRundownTimeGrouping || []).map((timeGrouping, index) => (
          <div key={index} className="grid grid-cols-3">
            <BubbleInput
              block
              type="text"
              className="p-4 sticky top-0"
              value={timeGrouping.name}
              placeholder="name"
              onValue={value => props.setTimeGroupingName(value, index)}
            />
            <BubbleInput
              block
              type="number"
              className="p-4 sticky top-0"
              value={timeGrouping.startTime}
              placeholder="startTime"
              onValue={value => props.setTimeGroupingStartTime(value, index)}
            />
            <div className="pt-4">
              <BubbleButton
                variant={'smoke'}
                onClick={() => props.handleTimeGroupingDelete(index)}
                icon="minus"
              ></BubbleButton>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-3">
          <BubbleInput
            block
            type="text"
            className="p-4 sticky top-0"
            value={timeGroupingName}
            placeholder="name"
            onValue={setTimeGroupingName}
          />
          <BubbleInput
            block
            type="number"
            className="p-4 sticky top-0"
            value={timeGroupingStartTime}
            placeholder="startTime"
            onValue={setTimeGroupingStartTime}
          />
          <div className="pt-4">
            <BubbleButton
              variant={'smoke'}
              disabled={!isValidTimeGrouping()}
              onClick={handleTimeGroupingAdd}
              icon="plus"
            >
              Add to group
            </BubbleButton>
          </div>
        </div>
      </div>
    );
  }
};

function getSegments(brand: Brand, segments: string[] = []): BubbleTypeaheadValue<BrandSegment>[] {
  const filteredSegments = brand.segments.filter(segment => segments.includes(segment._id));

  return filteredSegments.map(segment => ({
    key: segment._id,
    refKey: segment._id,
    item: segment,
  }));
}

function getLevels(brand: Brand, levels: string[] = []): BubbleTypeaheadValue<BrandLevel>[] {
  const filteredLevels = brand.levels.filter(level => levels.includes(level._id));

  return filteredLevels.map(level => ({
    key: level._id,
    refKey: level._id,
    item: level,
  }));
}

function getAttributes(
  brand: Brand,
  attributes: string[] = []
): BubbleTypeaheadValue<BrandAttribute>[] {
  const filteredAttributes = brand.attributes.filter(attribute =>
    attributes.includes(attribute._id)
  );

  return filteredAttributes.map(attribute => ({
    key: attribute._id,
    refKey: attribute._id,
    item: attribute,
  }));
}

function getVenues(brand: Brand, venues: string[] = []): BubbleTypeaheadValue<BrandVenueType>[] {
  const filteredVenues = brand.venueTypes.filter(venue => venues.includes(venue._id));

  return filteredVenues.map(venue => ({
    key: venue._id,
    refKey: venue._id,
    item: venue,
  }));
}
